import { useRef } from "react"
import { Employee } from "types/Employee"
import {
  CarrotPlanFormOption,
  MenuItemOptionFields,
  TopicOptionFields,
  useCarrotPlanMappings
} from "./useCarrotPlanMappings"
import { UnslottedStep } from "../CarrotPlanFormTypes"
import { NONE_VALUE } from "../JourneySelection"

type UseDynamicCarrotPlanMappingsArgs = {
  employee: Employee
  journey: number
  stage: number
  enableRecommendations?: boolean
}

type UseDynamicCarrotPlanMappingsResult = {
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  topics: CarrotPlanFormOption<TopicOptionFields>[]
  recommendedSteps: UnslottedStep[] | null
  recommendationsErrorStatus?: number
  isLoading: boolean
}

/**
 * Fetches and resolves the menu items + topics to display for the current (journey, stage).
 * Recommendations are fetched whenever `enableRecommendations` is true and both journey + stage are set,
 * keyed on the (journey, stage) tuple, concurrently with the options fetch.
 */
export function useDynamicCarrotPlanMappings({
  employee,
  journey,
  stage,
  enableRecommendations = false
}: UseDynamicCarrotPlanMappingsArgs): UseDynamicCarrotPlanMappingsResult {
  const initialSelectionRef = useRef({ journey, stage })

  const hasJourneySelectionChanged =
    journey !== initialSelectionRef.current.journey || stage !== initialSelectionRef.current.stage
  const bothJourneyAndStageSelected = journey !== NONE_VALUE && stage !== NONE_VALUE
  const shouldFetchNewOptions = hasJourneySelectionChanged && bothJourneyAndStageSelected
  const shouldFetchRecommendations = enableRecommendations && bothJourneyAndStageSelected

  // Fetch journey-specific options when the user has changed the selection or recommendations
  // are enabled; otherwise fetch general options.
  // Recommendations fetch concurrently in the same call.
  const effectiveJourney = shouldFetchNewOptions || shouldFetchRecommendations ? journey : undefined
  const effectiveStage = shouldFetchNewOptions || shouldFetchRecommendations ? stage : undefined

  const {
    mappedFormMenuItems,
    mappedFormTopics,
    recommendedSteps,
    recommendationsErrorStatus,
    areMappingsLoading
  } = useCarrotPlanMappings(employee, effectiveJourney, effectiveStage, shouldFetchRecommendations)

  return {
    menuItems: mappedFormMenuItems,
    topics: mappedFormTopics,
    recommendedSteps,
    recommendationsErrorStatus,
    isLoading: areMappingsLoading
  }
}
